import styled from '@emotion/styled';
import { format, parseISO } from 'date-fns';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSpring } from 'react-spring';

import HeaderContext, { APPROX_HEADER_HEIGHT } from '../components/Common/HeaderContext';
import ImageHero from '../components/Common/ImageHero';
import Layout from '../components/Common/Layout';
import SEO from '../components/Common/SEO';
import colors from '../styles/colors';
import theme from '../styles/theme';

const Post = styled('div')`
  max-width: 800px;
  margin: 0 auto 6rem;
`;

const Byline = styled('div')`
  margin: ${theme.spacing.standard}px 0;
  span:first-of-type {
    font-family: 'Apercu Pro Bold';
    color: ${theme.colors.Green100};
  }

  ${theme.breakpoints.tabletDown} {
    padding: 0 ${theme.spacing.small * 2}px;
  }
`;

const Copy = styled('div')`
  letter-spacing: 0.3px;
  font-size: 18px;
  line-height: 1.3em;

  h2 {
    font-size: 2em;
    line-height: 1.3em;
  }

  h3 {
    font-size: 1.7em;
    line-height: 1.3em;
  }

  h4 {
    font-size: 1.5em;
    line-height: 1.3em;
  }

  hr {
    margin: ${theme.spacing.small}px 0;
  }

  a {
    text-decoration: none;
    font-family: 'Apercu Pro Bold';
    color: ${theme.colors.Teal100};
  }

  strong {
    font-family: 'Apercu Pro Bold';
  }

  ${theme.breakpoints.tabletDown} {
    padding: 0 ${theme.spacing.small * 2}px;
  }
`;

const Link = styled('a')`
  color: ${colors.Teal100};
  font-style: italic;
`;

// @ts-ignore
/* tslint:disable */
const PostTemplate = ({ data, pageContext }) => {
  const post = data.wordpressPost;
  const metaUrl = `/blog/${format(parseISO(post.date), 'yyyy/MM/dd')}/${pageContext.slug}/`;

  const [, setY] = useSpring(() => ({ y: 0 }));
  const [scrollToOffset, setScrollToOffset] = useState(APPROX_HEADER_HEIGHT);

  useEffect(() => {
    document.title = `${post.title} - Kindur`;
  });

  // Define scrolling function
  const scrollToRef = (ref) => {
    const coords = ref.current.getBoundingClientRect();
    coords.y = coords.y || coords.top; // Fixes problem in Edge (MVP-1240)
    setY({
      y: window.scrollY + coords.y - scrollToOffset,
      reset: true,
      from: { y: window.scrollY },
      onFrame: (props) => {
        window.scroll(0, props.y);
      }
    });
  };

  // Define refs for scrolling
  const ref = {
    createAccount: useRef(null),
    howItWorks: useRef(null),
    ourFees: useRef(null),
    whyKindur: useRef(null)
  };

  // Bind scrolling functions
  const targets = {
    createAccount: scrollToRef.bind(null, ref.createAccount),
    howItWorks: scrollToRef.bind(null, ref.howItWorks),
    ourFees: scrollToRef.bind(null, ref.ourFees),
    whyKindur: scrollToRef.bind(null, ref.whyKindur)
  };

  // wp wraps the excerpt in paragraph html markup. Remove the markup to prevent it from interferring with twitter and facebook preview snippets
  const excerpt = `${post.excerpt}`.replace(/<p>/g, '').replace(/<\/p>/g, '');

  const renderDisclosure = ({acf}) => {
    if (!acf.kindur_blog_disclosure) {
      return null;
    }
    const { title, url, target } = acf.kindur_blog_disclosure;
    return <Link target={target} href={url}>{title}</Link>
  };

  // @ts-ignore
  return (
    <Layout pageContext={pageContext} targets={targets}>
      <SEO
        title={post.title}
        description={excerpt}
        image={post.featured_media.localFile.childImageSharp.fluid.src}
        url={metaUrl}
      />
      <HeaderContext.Consumer>
        {(headerHeight) => {
          setScrollToOffset(headerHeight);
          return (
            <Post>
              <ImageHero fluid={post.featured_media.localFile.childImageSharp.fluid} title={post.title} />
              <Byline>
                {format(parseISO(post.date), 'MMMM d, yyyy')}
              </Byline>
              <Copy dangerouslySetInnerHTML={{ __html: post.content }} />
              {renderDisclosure(post)}
            </Post>
          );
        }}
      </HeaderContext.Consumer>
    </Layout>
  );
};

PostTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  edges: PropTypes.array
};

export default PostTemplate;

export const pageQuery = graphql`
  query($id: String!) {
    wordpressPost(id: { eq: $id }) {
      excerpt
      title
      content
      date
      author {
        name
      }
      acf {
        kindur_blog_disclosure {
          title
          url
          target
        }
      }
      featured_media {
        localFile {
          childImageSharp {
            fluid(maxWidth: 1000) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
